import { type ComponentProps, useMemo } from "react";
import { Button } from "@volca/ui/components/ui/button";
import { ErrorState } from "@volca/ui/components/ui/error-state";
import { Skeleton } from "@volca/ui/components/ui/skeleton";
import { Stack } from "@volca/ui/components/ui/stack";
import { ReportCurrencyProvider } from "@/profitability/currency";
import { useReportOverview } from "@/profitability/overview/query";
import { useReportOutput } from "@/profitability/query";
import { objectsOf } from "@/profitability/records/objects";
import { RecordPanel } from "@/profitability/records/record-panel";
import { reportError, runDates } from "@/profitability/utils";
import { ReviewSpecProvider } from "./review-spec";
import type { Finding } from "./types";

/** The record and its related rows come from the run that supplied the finding. */
export const ReviewRecordPanel = ({
  finding,
  ...props
}: Omit<ComponentProps<typeof RecordPanel>, "objects" | "period"> & {
  finding: Finding;
}) => {
  const overview = useReportOverview(
    props.businessId,
    props.reportId,
    finding.run_id,
    null,
    !!finding.run_id,
  );
  const run = overview.data?.run;
  const output = useReportOutput(
    props.businessId,
    run?.interactive_available ? run.id : "",
  );
  const objects = useMemo(() => objectsOf(output.data), [output.data]);
  const failed = overview.isError ? overview : output.isError ? output : null;

  if (failed)
    return (
      <ErrorState
        title="The finding's report could not load"
        description={reportError(failed.error)}
      >
        <Button onClick={() => void failed.refetch()}>Try again</Button>
        <Button variant="ghost" onClick={props.onClose}>
          Close
        </Button>
      </ErrorState>
    );
  if (!finding.run_id || (run && !run.interactive_available))
    return (
      <ErrorState title="The saved report for this finding is unavailable">
        <Button variant="ghost" onClick={props.onClose}>
          Close
        </Button>
      </ErrorState>
    );
  if (overview.isPending || output.isPending || !run)
    return (
      <Stack
        gap={4}
        className="p-5"
        aria-busy
        aria-label="Loading the finding's record"
      >
        <Button
          variant="ghost"
          size="sm"
          className="self-start"
          onClick={props.onClose}
        >
          Close
        </Button>
        <Skeleton className="h-8 w-40" />
        <Skeleton className="h-32 w-full" />
      </Stack>
    );

  return (
    <ReportCurrencyProvider
      currency={output.data?.currency ?? overview.data?.currency}
    >
      <ReviewSpecProvider spec={overview.data?.review}>
        <RecordPanel
          {...props}
          objects={objects}
          period={{ runId: run.id, dates: runDates(run) }}
        />
      </ReviewSpecProvider>
    </ReportCurrencyProvider>
  );
};
